import { findTestByProductSlug } from 'components/Everywhere/helpers';
import { useCallback, useRef, useState } from 'react';
import { trackEva } from 'utils/eva';
import { z } from 'zod';

import productData from '../../../../eva-search-product-info.json';
import { safeParseZod } from '../lib/zod-helpers';

const SEARCH_API_URL = 'https://eva-search.everlywell.workers.dev';
const SEARCH_TIMEOUT_MS = 15_000;

export type SearchProductItem = {
  slug: string;
  description: string;
  kind: 'product' | 'everywhere';
};

const SlugsResponseSchema = z.object({
  products: z.array(z.string()),
});

export type SearchState = {
  query: string;
  results: SearchProductItem[];
  isSearching: boolean;
  isConnecting: boolean;
  error: string | null;
};

type EvaProduct = {
  slug: string;
  price?: number | null;
  biomarkers?: string[];
  symptoms?: string[];
  category?: string;
  type?: string;
  whatsMeasured?: string;
  /**
   * Approved copy to show on the search result card verbatim. When present it
   * replaces the generated "Tests …. For …." sentence, which only reads well
   * for products whose biomarkers are a list of lab markers.
   */
  cardDescription?: string;
};

const PRODUCTS: EvaProduct[] = (productData as { products: EvaProduct[] })
  .products;

const PRODUCT_MAP = new Map<string, EvaProduct>(
  PRODUCTS.map((p) => [p.slug, p]),
);

function buildBaseDescription(p: EvaProduct): string {
  const cardDescription = p.cardDescription?.trim();
  if (cardDescription) return cardDescription;

  const biomarkers = (p.biomarkers ?? []).filter(
    (b) =>
      !b.toLowerCase().includes('ingredients') &&
      !b.toLowerCase().includes('ingredient amounts'),
  );
  const symptoms = p.symptoms ?? [];

  const parts: string[] = [];
  if (biomarkers.length > 0) {
    parts.push(`Tests ${biomarkers.slice(0, 4).join(', ')}`);
  }
  if (symptoms.length > 0) {
    parts.push(`For ${symptoms.slice(0, 3).join(', ').toLowerCase()}`);
  }
  return parts.length > 0 ? `${parts.join('. ')}.` : '';
}

const BASE_DESCRIPTIONS = new Map<string, string>(
  PRODUCTS.map((p) => [p.slug, buildBaseDescription(p)]),
);

const NO_EVERYWHERE_PRODUCTS: readonly string[] = [];

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const MAX_MARKS = 3;

function markWord(
  text: string,
  word: string,
  budget: number,
): { text: string; used: number } {
  const pattern = new RegExp(`(?<![<\\w])(${escapeRegExp(word)}\\w*)`, 'gi');
  let used = 0;

  const marked = text.replace(pattern, (match) => {
    if (used >= budget) return match;

    used += 1;
    return `<mark>${match}</mark>`;
  });

  return { text: marked, used };
}

function highlightDescription(description: string, query: string): string {
  const words = query
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 2)
    .sort((a, b) => b.length - a.length);

  let result = description;
  let budget = MAX_MARKS;

  for (const word of words) {
    if (budget <= 0) break;

    const marked = markWord(result, word, budget);
    result = marked.text;
    budget -= marked.used;
  }

  return result;
}

export function useEvaTraditionalSearch(
  availableEverywhereProductSlugs: readonly string[] = NO_EVERYWHERE_PRODUCTS,
) {
  const [searchState, setSearchState] = useState<SearchState>({
    query: '',
    results: [],
    isSearching: false,
    isConnecting: false,
    error: null,
  });

  const abortRef = useRef<AbortController | null>(null);

  const submitQuery = useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) return;

      abortRef.current?.abort();

      setSearchState((prev) => ({
        ...prev,
        query: trimmed,
        results: [],
        isSearching: true,
        isConnecting: false,
        error: null,
      }));

      if (!SEARCH_API_URL) {
        setSearchState((prev) => ({
          ...prev,
          isSearching: false,
          error: 'Search is not configured.',
        }));
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;
      const timeout = setTimeout(() => controller.abort(), SEARCH_TIMEOUT_MS);

      const searchStart = performance.now();

      try {
        const response = await fetch(SEARCH_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: trimmed,
            available_everywhere_products: availableEverywhereProductSlugs,
          }),
          signal: controller.signal,
        });

        clearTimeout(timeout);
        const latencyMs = Math.round(performance.now() - searchStart);

        if (!response.ok) {
          trackEva('Eva Search Response', {
            query: trimmed,
            latency_ms: latencyMs,
            status: 'error',
            error_code: response.status,
            result_count: 0,
          });
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        const parsed = safeParseZod(SlugsResponseSchema, data);

        if (parsed.isOk()) {
          const items = parsed.value.products.flatMap<SearchProductItem>(
            (slug) => {
              if (PRODUCT_MAP.has(slug)) {
                return [
                  {
                    slug,
                    kind: 'product' as const,
                    description: highlightDescription(
                      BASE_DESCRIPTIONS.get(slug) ?? '',
                      trimmed,
                    ),
                  },
                ];
              }

              const everywhereTest = findTestByProductSlug(slug);
              if (
                !everywhereTest ||
                !availableEverywhereProductSlugs.includes(slug)
              ) {
                return [];
              }

              return [
                {
                  slug,
                  kind: 'everywhere' as const,
                  description: highlightDescription(
                    everywhereTest.desc,
                    trimmed,
                  ),
                },
              ];
            },
          );

          trackEva('Eva Search Response', {
            query: trimmed,
            latency_ms: latencyMs,
            status: 'success',
            result_count: items.length,
            result_slugs: items.map((i) => i.slug),
          });

          setSearchState((prev) => ({
            ...prev,
            results: items,
            isSearching: false,
          }));
        } else {
          trackEva('Eva Search Response', {
            query: trimmed,
            latency_ms: latencyMs,
            status: 'parse_error',
            result_count: 0,
          });
          setSearchState((prev) => ({
            ...prev,
            isSearching: false,
            error: 'Failed to parse search results.',
          }));
        }
      } catch (error: unknown) {
        clearTimeout(timeout);
        if (error instanceof DOMException && error.name === 'AbortError') {
          trackEva('Eva Search Response', {
            query: trimmed,
            latency_ms: Math.round(performance.now() - searchStart),
            status: 'timeout',
            result_count: 0,
          });
          setSearchState((prev) => ({
            ...prev,
            isSearching: false,
            error: 'Search timed out. Please try again.',
          }));
        } else {
          setSearchState((prev) => ({
            ...prev,
            isSearching: false,
            error: 'Something went wrong. Please try again.',
          }));
        }
      }
    },
    [availableEverywhereProductSlugs],
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setSearchState({
      query: '',
      results: [],
      isSearching: false,
      isConnecting: false,
      error: null,
    });
  }, []);

  return {
    ...searchState,
    submitQuery,
    reset,
  };
}
